import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import BenevolCheckLogo from '../components/BenevolCheckLogo';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="container mx-auto px-4 py-4 sm:py-6">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <BenevolCheckLogo size="md" />
          <div className="flex gap-2 sm:gap-3 w-full sm:w-auto justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/login')}
              data-testid="login-button"
              className="flex-1 sm:flex-initial"
            >
              Connexion
            </Button>
            <Button 
              onClick={() => navigate('/register')}
              data-testid="register-button"
              className="flex-1 sm:flex-initial"
            >
              S'inscrire
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="container mx-auto px-4 py-8 sm:py-16">
        <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16">
          <div className="order-2 md:order-1">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-indigo-900 mb-4 sm:mb-6">
              Valorisez vos compétences bénévoles
            </h2>
            <p className="text-lg sm:text-xl text-gray-700 mb-6 sm:mb-8">
              Créez, validez et partagez facilement vos attestations de compétences
              acquises lors de vos missions bénévoles.
            </p>
            <Button 
              size="lg" 
              className="text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
              onClick={() => navigate('/register')}
              data-testid="get-started-button"
            >
              Commencer maintenant
            </Button>
          </div>
          <div className="relative order-1 md:order-2">
            <img 
              src="https://images.unsplash.com/photo-1654609106185-0876cd0b417b?w=800&q=80" 
              alt="Un homme et une femme prenant un selfie avec un téléphone portable"
              className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              style={{ maxHeight: '400px' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/20 to-transparent rounded-2xl"></div>
          </div>
        </div>

        {/* Features */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 mt-12 sm:mt-16">
          <Card className="p-4 sm:p-6" data-testid="feature-create">
            <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">📝</div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3 text-indigo-900">Créez vos attestations</h3>
            <p className="text-sm sm:text-base text-gray-600">
              Remplissez facilement vos attestations avec les compétences développées
              lors de vos missions bénévoles.
            </p>
          </Card>

          <Card className="p-4 sm:p-6" data-testid="feature-validate">
            <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">✅</div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3 text-indigo-900">Faites-les valider</h3>
            <p className="text-sm sm:text-base text-gray-600">
              Les responsables associatifs peuvent valider vos attestations
              en quelques clics.
            </p>
          </Card>

          <Card className="p-4 sm:p-6 sm:col-span-2 md:col-span-1" data-testid="feature-share">
            <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">🎯</div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3 text-indigo-900">Partagez-les</h3>
            <p className="text-sm sm:text-base text-gray-600">
              Téléchargez, imprimez ou partagez vos attestations validées
              avec vos futurs employeurs.
            </p>
          </Card>
        </div>

        {/* How it works */}
        <div className="mt-16 sm:mt-20">
          <h3 className="text-2xl sm:text-3xl font-bold text-center text-indigo-900 mb-8 sm:mb-12">
            Comment ça marche ?
          </h3>
          <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
            <div className="flex gap-3 sm:gap-4 items-start">
              <div className="bg-indigo-600 text-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center font-bold flex-shrink-0 text-sm sm:text-base">
                1
              </div>
              <div>
                <h4 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">Inscrivez-vous</h4>
                <p className="text-sm sm:text-base text-gray-600">
                  Créez votre compte en tant que bénévole ou responsable associatif.
                </p>
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4 items-start">
              <div className="bg-indigo-600 text-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center font-bold flex-shrink-0 text-sm sm:text-base">
                2
              </div>
              <div>
                <h4 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">Créez votre attestation</h4>
                <p className="text-sm sm:text-base text-gray-600">
                  Remplissez les informations sur votre mission et sélectionnez les compétences développées.
                </p>
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4 items-start">
              <div className="bg-indigo-600 text-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center font-bold flex-shrink-0 text-sm sm:text-base">
                3
              </div>
              <div>
                <h4 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">Validation</h4>
                <p className="text-sm sm:text-base text-gray-600">
                  Le responsable associatif valide votre attestation pour la rendre officielle.
                </p>
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4 items-start">
              <div className="bg-indigo-600 text-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center font-bold flex-shrink-0 text-sm sm:text-base">
                4
              </div>
              <div>
                <h4 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">Utilisez votre attestation</h4>
                <p className="text-sm sm:text-base text-gray-600">
                  Téléchargez, imprimez ou partagez votre attestation validée.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-indigo-900 text-white mt-16 sm:mt-20 py-6 sm:py-8">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-center gap-6 mb-4">
            <Link to="/contact" className="hover:underline text-white/90 hover:text-white">Contact</Link>
            <span className="text-white/50">|</span>
            <Link to="/cgu" className="hover:underline text-white/90 hover:text-white">CGU</Link>
            <span className="text-white/50">|</span>
            <Link to="/confidentialite" className="hover:underline text-white/90 hover:text-white">Confidentialité</Link>
          </div>
          <p className="text-sm sm:text-base text-white/80">&copy; 2025 Bénévol'Check - Propulsé par France Bénévolat avec l'aide du SDJES Côte d'Or</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;